import * as React from 'react';
import { Panel } from '../Panel';
import { ICONS } from '../../../assets/Icons';
import { graphStyle } from '../../../assets/styles/Layout/AreaGraph';
import { CONSTANTS } from '../../../assets/UIConstants';
import Line from '../svgs/Line';
import SegmentalMan from '../svgs/SegmentalMan';
import { BalanceValue } from '../BalanceValue';
import { PanelFooter } from '../PanelFooter';
import {
    BALANCED,
    BOTTOM_HEAVY, RL_IMBALANCE,
    SEGMENTAL_ANALYSIS_INFO, SEGMENTAL_ANALYSIS_PANEL_TITLE,
    SEGMENTAL_ANALYSIS_POPOVER_TITLE, TOP_HEAVY
} from '../../../assets/ApplicationCopy';
import { ScanStore } from '../../../data_layer/stores/ScanStore';
import { LeftArmMiniGraph, LeftLegMiniGraph, RightArmMiniGraph, RightLegMiniGraph } from './MiniGraphs';
import { computed } from 'mobx';
import { ApplicationStore } from '../../../data_layer/stores/ApplicationStore';
import { inject } from 'mobx-react';
import * as assert from "assert";

interface Props {
    scanStore: ScanStore;
    store?: ApplicationStore;
}


@inject('store')
export class SegmentalLeanAnalysis extends React.Component<Props> {

    @computed private get armsAssessment(): string | undefined {
        const scanResults = this.props.scanStore.mostRecentScan.scan_results;
        const difference = scanResults.fatFreeMassPoundsLeftArm - scanResults.fatFreeMassPoundsRightArm;
        if (Math.abs(difference) > 0.5) {
            return RL_IMBALANCE;
        }
        return undefined;
    }

    @computed private get legsAssessment(): string | undefined {
        const scanResults = this.props.scanStore.mostRecentScan.scan_results;
        const difference = scanResults.fatFreeMassPoundsLeftLeg - scanResults.fatFreeMassPoundsRightLeg;
        if (Math.abs(difference) > 0.5) {
            return RL_IMBALANCE;
        }
        return undefined;
    }

    private getArmsTotalPercent(): number {
        const scanResults = this.props.scanStore.mostRecentScan.scan_results;
        return scanResults.fatFreeMassPercentRightArm + scanResults.fatFreeMassPercentLeftArm;
    }

    private getLegsTotalPercent(): number {
        const scanResults = this.props.scanStore.mostRecentScan.scan_results;
        return scanResults.fatFreeMassPercentRightLeg + scanResults.fatFreeMassPercentLeftLeg;
    }

    @computed private get topBottomBalance(): string | undefined {
        if (undefined == this.props.store || false == this.props.store.storeIsReady) {
            throw new Error('SegmentalLeanAnalysis#topBottomBalance - ApplicationStore not available');
        }
        const user = this.props.store.authorizationStore.userProfile;
        const gender = user.sex.toLowerCase();
        switch (gender) {
            case 'male':
                return this.maleTopBottomBalance();
            case 'female':
                return this.femaleTopBottomBalance();
            default:
                assert(false, 'SegmentalLeanAnalysis#topBottomBalance: unmatched user sex');
                return undefined;
        }
    }

    private maleTopBottomBalance(): string | undefined {
        const arms: number = this.getArmsTotalPercent();
        const legs: number = this.getLegsTotalPercent();

        if ((arms > 380 && legs < 300)
            || (arms > 350 && legs < 280)
            || (arms > 320 && legs < 260)
            || (arms > 290 && legs < 240)
            || (arms > 260 && legs < 220)
            || (arms > 230 && legs < 200)
            || (arms > 200 && legs < 180)
            || (arms > 170 && legs < 160)
        ) {
            return TOP_HEAVY;
        }

        if ((legs > 340 && arms < 380)
            || (legs > 320 && arms < 350)
            || (legs > 300 && arms < 320)
            || (legs > 280 && arms < 290)
            || (legs > 260 && arms < 260)
            || (legs > 240 && arms < 230)
            || (legs > 220 && arms < 200)
            || (legs > 200 && arms < 170)
            || (legs > 180 && arms < 140)
        ) {
            return BOTTOM_HEAVY;
        }

        return undefined;
    }

    private femaleTopBottomBalance(): string | undefined {
        const arms: number = this.getArmsTotalPercent();
        const legs: number = this.getLegsTotalPercent();

        if ((arms > 400 && legs < 280)
            || (arms > 360 && legs < 260)
            || (arms > 320 && legs < 240)
            || (arms > 280 && legs < 220)
            || (arms > 240 && legs < 200)
            || (arms > 200 && legs < 180)
            || (arms > 160 && legs < 160)
        ) {
            return TOP_HEAVY;
        }

        if ((legs > 300 && arms < 360)
            || (legs > 280 && arms < 320)
            || (legs > 260 && arms < 280)
            || (legs > 240 && arms < 240)
            || (legs > 220 && arms < 200)
            || (legs > 200 && arms < 160)
            || (legs > 180 && arms < 120)
        ) {
            return BOTTOM_HEAVY;
        }

        return undefined;
    }

    @computed private get overallAssessment(): string {
        return this.topBottomBalance
            || this.armsAssessment
            || this.legsAssessment
            || BALANCED;
    }

    public render(): JSX.Element {
        return (
            <Panel
                id={'segmental-lean-analysis'} 
                width={500} 
                height={515} 
                title={SEGMENTAL_ANALYSIS_PANEL_TITLE}
                icon={ICONS.QUESTION_MARK}
                popoverTitle={SEGMENTAL_ANALYSIS_POPOVER_TITLE}
                popoverBody={SEGMENTAL_ANALYSIS_INFO}
            >
                <div style={{flexDirection: 'column', flexGrow: 1}}>
                    <div style={{display: 'flex', flexDirection: 'row', paddingBottom: 30}}>
                        <div style={...graphStyle.container}>
                            <LeftArmMiniGraph
                                scanStore={this.props.scanStore}
                                paddingTop={30}
                            />
                            <LeftLegMiniGraph
                                scanStore={this.props.scanStore}
                                paddingTop={50}
                                color={CONSTANTS.COLORS.THEME_ORANGE}
                            />
                            <div style={balanceValueContainerStyle}>
                                <BalanceValue
                                    title={'Arms'}
                                    value={this.armsAssessment || BALANCED}
                                    fontSize={18}
                                    color={CONSTANTS.COLORS.THEME_GREY}
                                />
                            </div>
                        </div>
                        <div style={graphStyle.container}>
                            <div>
                                <Line
                                    width={10}
                                    height={75}
                                    color={CONSTANTS.COLORS.BORDER_GREY}
                                    strokeWidth={2}
                                />
                            </div>
                            <div style={{margin: 10}}>
                                <SegmentalMan
                                    height={100}
                                    color={CONSTANTS.COLORS.THEME_GREY}
                                />
                            </div>
                            <div>
                                <Line
                                    width={10}
                                    height={75}
                                    color={CONSTANTS.COLORS.BORDER_GREY}
                                    strokeWidth={2}
                                />
                            </div>
                        </div>
                        <div style={...graphStyle.container}>
                            <RightArmMiniGraph
                                scanStore={this.props.scanStore}
                                paddingTop={30}
                            />
                            <RightLegMiniGraph
                                scanStore={this.props.scanStore}
                                paddingTop={50}
                                color={CONSTANTS.COLORS.THEME_ORANGE}
                            />
                            <div style={balanceValueContainerStyle}>
                                <BalanceValue
                                    title={'Legs'}
                                    value={this.legsAssessment || BALANCED}
                                    fontSize={18}
                                    color={CONSTANTS.COLORS.THEME_GREY}
                                />
                            </div>
                        </div>
                    </div>
                    <PanelFooter>
                        <div style={overallBalanceValueContainerStyle}>
                            <BalanceValue
                                title={'Overall'}
                                value={this.overallAssessment}
                                fontSize={24}
                                color={CONSTANTS.COLORS.DARKEST_GREY}
                            />
                        </div>
                    </PanelFooter>
                </div>
            </Panel>
        );
    }
}

const balanceValueContainerStyle: React.CSSProperties = {
    marginTop: 20 
};

const overallBalanceValueContainerStyle: React.CSSProperties = {
    flexDirection: CONSTANTS.FLEX_DIRECTION.ROW, 
    alignItems: CONSTANTS.ALIGN_ITEMS.CENTER, 
    display: CONSTANTS.DISPLAY.FLEX, 
    justifyContent: CONSTANTS.FLEX_JUSTIFY.CENTER
};


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/views/components/dashboardPanels/SegmentalLeanAnalysis.tsx